(() => {
  'use strict';

  if (customElements.get('crystal-snow')) return;

  class CrystalSnow extends HTMLElement {
    static get observedAttributes() {
      return ['data-enabled', 'data-mobile', 'data-density', 'data-mobile-density', 'data-speed', 'data-size', 'data-opacity', 'data-color', 'data-layer', 'data-position', 'data-offset', 'data-exclude', 'data-pause'];
    }

    connectedCallback() {
      if (this.controller || document.querySelector('crystal-snow') !== this) return;
      this.canvas = this.querySelector('canvas');
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-snow-label]');
      this.ctx = this.canvas?.getContext('2d', { alpha: true });
      if (!this.ctx || !this.button || !this.label) return;
      this.controller = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.particles = [];
      this.frameId = 0;
      this.lastTime = 0;
      this.dirtyRects = true;
      this.exclusions = [];
      this.designMode = Boolean(window.Shopify?.designMode);
      this.paused = false;
      if (!this.designMode) {
        try { this.paused = sessionStorage.getItem('crystal-snow:paused:v1') === '1'; } catch {}
      }
      const signal = this.controller.signal;
      this.button.addEventListener('click', () => {
        this.paused = !this.paused;
        if (!this.designMode) {
          try { sessionStorage.setItem('crystal-snow:paused:v1', this.paused ? '1' : '0'); } catch {}
        }
        this.reconcile();
      }, { signal });
      this.onEnvironmentChange = () => { this.resize(); this.reconcile(); };
      this.motion.addEventListener('change', this.onEnvironmentChange);
      this.mobile.addEventListener('change', this.onEnvironmentChange);
      window.addEventListener('resize', this.onEnvironmentChange, { passive: true, signal });
      window.visualViewport?.addEventListener('resize', this.onEnvironmentChange, { passive: true, signal });
      document.addEventListener('scroll', () => { this.dirtyRects = true; }, { passive: true, capture: true, signal });
      document.addEventListener('visibilitychange', () => this.reconcile(), { signal });
      window.addEventListener('pagehide', () => this.stop(), { signal });
      window.addEventListener('pageshow', () => this.onEnvironmentChange(), { signal });
      this.observer = new MutationObserver((records) => {
        if (!records.some((record) => !this.contains(record.target))) return;
        this.dirtyRects = true;
        if (this.mutationTimer) return;
        this.mutationTimer = setTimeout(() => {
          this.mutationTimer = 0;
          if (this.isConnected) this.reconcile();
        }, 120);
      });
      this.observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'class', 'style', 'aria-modal', 'aria-expanded', 'data-snow-pause'] });
      this.configure();
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.mutationTimer);
      this.mutationTimer = 0;
      this.observer?.disconnect();
      this.controller?.abort();
      this.motion?.removeEventListener('change', this.onEnvironmentChange);
      this.mobile?.removeEventListener('change', this.onEnvironmentChange);
      this.controller = null;
      this.particles = [];
      this.sprites = [];
      this.spriteColor = null;
    }

    attributeChangedCallback() {
      if (this.controller) this.configure();
    }

    number(name, fallback, min, max) {
      const raw = this.dataset[name];
      const value = raw === undefined || raw === '' ? fallback : Number(raw);
      return Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
    }

    configure() {
      this.config = {
        density: this.number('density', 45, 10, 90),
        mobileDensity: this.number('mobileDensity', 18, 5, 30),
        speed: this.number('speed', 100, 40, 180) / 100,
        size: this.number('size', 100, 60, 150) / 100,
        opacity: this.number('opacity', 75, 20, 100) / 100,
        color: /^#[\da-f]{6}$/i.test(this.dataset.color || '') ? this.dataset.color : '#ffffff'
      };
      this.style.setProperty('--cs-layer', this.number('layer', 20, 0, 100));
      this.style.setProperty('--cs-offset', `${this.number('offset', 20, 8, 160)}px`);
      const position = this.dataset.position === 'right' ? 'right' : 'left';
      if (this.dataset.position !== position) this.dataset.position = position;
      if (this.spriteColor !== this.config.color) this.createSprites();
      this.resize();
      this.reconcile();
    }

    resize() {
      if (!this.ctx || !this.config) return;
      this.width = Math.max(1, window.innerWidth);
      this.height = Math.max(1, window.innerHeight);
      const small = this.mobile.matches;
      const budget = small ? 1200000 : 3000000;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(budget / (this.width * this.height)));
      this.canvas.width = Math.max(1, Math.round(this.width * ratio));
      this.canvas.height = Math.max(1, Math.round(this.height * ratio));
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const count = small
        ? this.config.mobileDensity
        : Math.round(Math.min(90, Math.max(10, this.config.density * Math.min(1.5, Math.max(0.5, this.width * this.height / 1296000)))));
      this.particles = Array.from({ length: count }, (_, index) => this.makeParticle(true, index < Math.round(count * 0.8)));
      this.dirtyRects = true;
      this.lastTime = 0;
    }

    createSprites() {
      this.spriteColor = this.config.color;
      this.sprites = Array.from({ length: 3 }, (_, variant) => {
        const sprite = document.createElement('canvas');
        sprite.width = 96;
        sprite.height = 96;
        const ctx = sprite.getContext('2d');
        if (!ctx) return null;
        ctx.translate(48, 48);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        for (let arm = 0; arm < 6; arm += 1) {
          const angle = arm * Math.PI / 3;
          const point = (x, y) => [Math.cos(angle) * x - Math.sin(angle) * y, Math.sin(angle) * x + Math.cos(angle) * y];
          ctx.moveTo(0, 0);
          ctx.lineTo(...point(0, -38));
          const fork = variant === 1 ? -23 : -21;
          ctx.moveTo(...point(0, fork));
          ctx.lineTo(...point(-9, fork - 10));
          ctx.moveTo(...point(0, fork));
          ctx.lineTo(...point(9, fork - 10));
          if (variant === 2) {
            ctx.moveTo(...point(0, -11));
            ctx.lineTo(...point(-6, -18));
            ctx.moveTo(...point(0, -11));
            ctx.lineTo(...point(6, -18));
          }
        }
        ctx.globalAlpha = 0.18;
        ctx.strokeStyle = '#70808b';
        ctx.lineWidth = 5;
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = this.config.color;
        ctx.lineWidth = variant === 1 ? 3.4 : 3;
        ctx.stroke();
        return sprite;
      });
    }

    makeParticle(initial = false, isFlake = true) {
      const depth = Math.random();
      return {
        x: Math.random() * this.width,
        y: initial ? Math.random() * this.height : -16,
        isFlake,
        variant: Math.floor(Math.random() * 3),
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.44,
        radius: (isFlake ? 4.5 + depth * 4 : 0.65 + depth * 1.8) * this.config.size,
        velocity: (13 + depth * 23) * this.config.speed,
        alpha: (isFlake ? 0.48 + depth * 0.47 : 0.3 + depth * 0.65) * this.config.opacity,
        phase: Math.random() * Math.PI * 2,
        sway: 3 + Math.random() * 8,
        frequency: 0.35 + Math.random() * 0.45
      };
    }

    elements(selector) {
      if (!selector) return [];
      try { return Array.from(document.querySelectorAll(selector)); } catch { return []; }
    }

    visible(element) {
      if (this.contains(element)) return false;
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < this.height && rect.left < this.width && style.visibility !== 'hidden' && style.display !== 'none' && style.opacity !== '0';
    }

    reconcile() {
      if (!this.config || !this.isConnected) return;
      const blocked = this.elements(this.dataset.pause).some((element) => this.visible(element));
      const unavailable = this.dataset.enabled !== 'true' || this.motion.matches || (this.mobile.matches && this.dataset.mobile !== 'true') || document.hidden || blocked;
      this.hidden = unavailable;
      this.button.hidden = unavailable;
      const action = this.paused ? this.dataset.resumeLabel : this.dataset.pauseLabel;
      this.label.textContent = action;
      this.button.setAttribute('aria-label', action);
      this.button.setAttribute('title', action);
      this.canvas.hidden = unavailable || this.paused;
      if (unavailable || this.paused) {
        this.stop();
        return;
      }
      if (!this.frameId) {
        this.lastTime = 0;
        this.frameId = requestAnimationFrame((time) => this.frame(time));
      }
    }

    stop() {
      if (this.frameId) cancelAnimationFrame(this.frameId);
      this.frameId = 0;
      this.lastTime = 0;
      this.ctx?.clearRect(0, 0, this.width || 1, this.height || 1);
    }

    frame(time) {
      this.frameId = 0;
      if (!this.isConnected || this.hidden || this.paused) return;
      if (this.lastTime && time - this.lastTime < 1000 / 30 - 0.5) {
        this.frameId = requestAnimationFrame((next) => this.frame(next));
        return;
      }
      const delta = this.lastTime ? Math.min((time - this.lastTime) / 1000, 0.1) : 0;
      this.lastTime = time;
      if (this.dirtyRects) {
        this.exclusions = this.elements(this.dataset.exclude).filter((element) => this.visible(element)).map((element) => element.getBoundingClientRect());
        this.dirtyRects = false;
      }
      const ctx = this.ctx;
      ctx.clearRect(0, 0, this.width, this.height);
      ctx.fillStyle = this.config.color;
      for (let index = 0; index < this.particles.length; index += 1) {
        let particle = this.particles[index];
        particle.phase += delta * particle.frequency;
        particle.y += delta * particle.velocity;
        particle.x += Math.sin(particle.phase) * delta * particle.sway;
        particle.angle += delta * particle.spin;
        if (particle.y > this.height + 16) particle = this.particles[index] = this.makeParticle(false, particle.isFlake);
        if (particle.x < -16) particle.x = this.width + 16;
        if (particle.x > this.width + 16) particle.x = -16;
        ctx.globalAlpha = particle.alpha;
        const sprite = particle.isFlake && this.sprites[particle.variant];
        if (sprite) {
          ctx.save();
          ctx.translate(particle.x, particle.y);
          ctx.rotate(particle.angle);
          ctx.drawImage(sprite, -particle.radius, -particle.radius, particle.radius * 2, particle.radius * 2);
          ctx.restore();
          continue;
        }
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = particle.alpha * 0.17;
        ctx.strokeStyle = '#70808b';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      for (const rect of this.exclusions) ctx.clearRect(rect.left - 2, rect.top - 2, rect.width + 4, rect.height + 4);
      this.frameId = requestAnimationFrame((next) => this.frame(next));
    }
  }

  customElements.define('crystal-snow', CrystalSnow);
})();
